import React, { useEffect, useState } from 'react'
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControl,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material'
import { AppCardTitle, AppResultsSkeleton, AppNote } from '@components'
import { dsColorNew } from '@palette'
import { MbPage } from 'Pages/MyBusiness/_shared/Ui'

import { AdminProgress, adminCardSx, adminFieldsSx, adminHintSx, rowToneSx } from './ui'

/**
 * Generic two-list mapping admin.
 *
 * The PMS side is fetched via GET <listEndpoint> and shown as table
 * rows. Each row carries the current mapping value (if any) and an
 * editable dropdown built from `psOptions`. A PATCH <listEndpoint>/<rowKey>
 * fires on change with `{ [valueField]: newValue }` in the body.
 *
 * Used by Office / Category / Agency mapping admins — they all share
 * the same shape (PMS-side table + PS-side dropdown + last-12m volume).
 *
 *   <MappingAdminPage
 *      icon="location-dot"                 // FA icon of the card title
 *      title="Office Mapping"
 *      subtitle="Map PMS offices to Price Seeker offices"
 *      listEndpoint="/api/my-business/admin/office-mapping"
 *      rowKey="code"                       // primary key on each PMS row
 *      rowKeyParam="pms_office_code"       // URL segment for PATCH
 *      pmsListKey="offices"                // payload[pmsListKey] = rows[]
 *      valueField="ps_master_office_id"    // field carrying the mapped PS id
 *      valueIsNumber                       // ps id is int → cast on save
 *      psOptionsKey="ps_options"           // payload[psOptionsKey] = options[]
 *      psOptionId="id"                     // dropdown value
 *      psOptionLabel={(opt) => `${opt.iata} · ${opt.name}`}
 *      pmsColumns={[
 *        { label: 'PMS code', cell: (r) => r.code },
 *        { label: 'Name',     cell: (r) => r.name },
 *      ]}
 *   />
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtEur = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR',
		minimumFractionDigits: 0, maximumFractionDigits: 0,
	}).format(n)

export default function MappingAdminPage({
	icon,
	title,
	subtitle,
	listEndpoint,
	rowKey,
	rowKeyParam,
	pmsListKey,
	valueField,
	valueIsNumber = false,
	psOptionsKey = 'ps_options',
	psOptionId = 'id',
	psOptionLabel,
	pmsColumns,
	footerHint,
	embedded = false,  // when true, skip MbPage — host (e.g. Admin tabs) supplies it
	autoMapEndpoint,   // optional POST endpoint that auto-fills obvious mappings
	autoMapLabel = 'Auto-map matching',
	autoMapHint  = 'Fill in the entries where the PMS code matches a Price Seeker option 1:1.',
}) {
	const [data, setData] = useState(null)
	const [error, setError] = useState(null)
	const [loading, setLoading] = useState(true)
	const [savingKey, setSavingKey] = useState(null)
	const [savedAt, setSavedAt] = useState({})
	const [automapping, setAutomapping] = useState(false)
	const [automapMsg,  setAutomapMsg]  = useState(null)

	const fetchAll = () => {
		setLoading(true); setError(null)
		fetch(listEndpoint, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}

	useEffect(() => { fetchAll() }, [listEndpoint])

	const runAutoMap = () => {
		if (!autoMapEndpoint) return
		setAutomapping(true); setError(null); setAutomapMsg(null)
		fetch(autoMapEndpoint, { method: 'POST', credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then((res) => {
				setAutomapMsg(res.mapped > 0
					? `Auto-mapped ${res.mapped} ${res.mapped === 1 ? 'entry' : 'entries'}.`
					: 'No new matches found.')
				fetchAll()
			})
			.catch(e => setError(e.message))
			.finally(() => setAutomapping(false))
	}

	const updateMapping = (row, newValue) => {
		const key = row[rowKey]
		setSavingKey(key)
		const payload = { [valueField]:
			newValue === '' ? null
			: valueIsNumber ? Number(newValue)
			: newValue
		}
		fetch(`${listEndpoint}/${encodeURIComponent(key)}`, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify(payload),
		})
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(() => {
				setData((prev) => ({
					...prev,
					[pmsListKey]: prev[pmsListKey].map(r =>
						r[rowKey] === key ? { ...r, [valueField]: payload[valueField] } : r
					),
				}))
				setSavedAt((prev) => ({ ...prev, [key]: Date.now() }))
			})
			.catch(e => setError(e.message))
			.finally(() => setSavingKey(null))
	}

	const rows       = data?.[pmsListKey] || []
	const psOptions  = data?.[psOptionsKey] || []
	const mappedRows = rows.filter(r => r[valueField] != null).length
	const totalRows  = rows.length
	const unmapped   = totalRows - mappedRows
	const totalBookings    = rows.reduce((s, r) => s + (r.bookings || 0), 0)
	const unmappedBookings = rows
		.filter(r => r[valueField] == null)
		.reduce((s, r) => s + (r.bookings || 0), 0)

	const body = (
		<>
			<AdminProgress active={loading}/>

			<Box>
				{error && <AppNote severity="error" sx={{ mb: 2 }}>{error}</AppNote>}

				{loading && !data && <AppResultsSkeleton columns={pmsColumns.length + 3}/>}

				{data && (
					<Paper sx={{ ...adminCardSx, ...adminFieldsSx }}>
						{/* Header: the title, then how much is mapped and what still flows through the gaps */}
						<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
							<AppCardTitle icon={icon} title={title}/>
							{autoMapEndpoint && (
								<Box display="flex" alignItems="center" gap={1} sx={{ ml: 'auto' }}>
									{automapMsg && (
										<Typography sx={adminHintSx}>
											{automapMsg}
										</Typography>
									)}
									<Button variant="outlined" size="small"
									        onClick={runAutoMap}
									        disabled={automapping || psOptions.length === 0}>
										{automapping ? 'Mapping…' : autoMapLabel}
									</Button>
								</Box>
							)}
						</Box>

						<Box display="flex" alignItems="center" gap={3} flexWrap="wrap" sx={{ mt: 1.5, mb: 2 }}>
							<Box>
								<Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: dsColorNew.textTitle }}>
									{mappedRows} <Box component="span"
									                  sx={{ fontSize: 15, color: dsColorNew.textMuted, fontWeight: 500 }}>
										/ {totalRows} mapped
									</Box>
								</Typography>
								<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 0.25 }}>
									{unmapped > 0
										? `${unmapped} still unmapped`
										: 'All entries mapped'}
								</Typography>
							</Box>
							{unmappedBookings > 0 && totalBookings > 0 && (
								<Typography sx={{
									fontSize: 13, px: 1.25, py: 0.5, borderRadius: '10px',
									backgroundColor: dsColorNew.noteAmberBg, border: `1px solid ${dsColorNew.noteAmberBorder}`,
								}}>
									<Box component="span" sx={{ fontWeight: 700, color: dsColorNew.noteAmber }}>
										{fmtNum(unmappedBookings)}
									</Box>
									<Box component="span" sx={{ color: dsColorNew.textDark }}>
										{' '}bookings (
										{Math.round(unmappedBookings / totalBookings * 100) || 0}%) flow
										through unmapped entries
									</Box>
								</Typography>
							)}
							{psOptions.length === 0 && (
								<AppNote severity="warning" sx={{ flex: 1 }}>
									No Price Seeker options found for your user. Confirm your PS
									scope includes the catalog needed for this mapping.
								</AppNote>
							)}
						</Box>
						{autoMapEndpoint && (
							<Typography sx={{ ...adminHintSx, mt: -1, mb: 2, fontStyle: 'italic' }}>
								{autoMapHint}
							</Typography>
						)}

						<TableContainer>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										{pmsColumns.map((c, i) => (
											<TableCell key={i}>{c.label}</TableCell>
										))}
										<TableCell align="right">Bookings (12m)</TableCell>
										<TableCell align="right">Revenue (12m)</TableCell>
										<TableCell sx={{ minWidth: 280 }}>
											Mapped to Price Seeker
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((r) => {
										const justSaved = savedAt[r[rowKey]] &&
										                  Date.now() - savedAt[r[rowKey]] < 1500
										const currentValue = r[valueField] ?? ''
										const isUnmapped = r[valueField] == null
										return (
											<TableRow key={r[rowKey]} hover
											          sx={rowToneSx({ saved: justSaved })}>
												{pmsColumns.map((c, i) => (
													<TableCell key={i}>{c.cell(r)}</TableCell>
												))}
												<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
													{fmtNum(r.bookings || 0)}
												</TableCell>
												<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
													{fmtEur(r.revenue || 0)}
												</TableCell>
												<TableCell>
													<Box display="flex" alignItems="center" gap={1}>
														<FormControl size="small" sx={{ minWidth: 240 }}>
															<Select value={currentValue}
															        displayEmpty
															        disabled={savingKey === r[rowKey] ||
															                  psOptions.length === 0}
															        onChange={(e) => updateMapping(r, e.target.value)}
															        renderValue={(v) => {
																        if (v === '' || v == null) {
																            return <Box sx={{
																                color: dsColorNew.textSubtle,
																                fontStyle: 'italic',
																                fontSize: 13,
																            }}>unmapped</Box>
																        }
																        const opt = psOptions.find(o => o[psOptionId] === v)
																        return opt
																            ? <Chip size="small" color="info" variant="outlined"
																                    label={psOptionLabel(opt)}
																                    sx={{ fontWeight: 600, maxWidth: 1 }}/>
																            : <em>{String(v)}</em>
															        }}>
																<MenuItem value="">
																	<em>unmapped</em>
																</MenuItem>
																{psOptions.map((opt) => (
																	<MenuItem key={opt[psOptionId]} value={opt[psOptionId]}>
																		{psOptionLabel(opt)}
																	</MenuItem>
																))}
															</Select>
														</FormControl>
														{savingKey === r[rowKey] && <CircularProgress size={16}/>}
														{isUnmapped && !justSaved && (
															<Chip size="small" variant="outlined" color="warning"
															      label="unmapped"
															      sx={{ fontSize: 11.5 }}/>
														)}
													</Box>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						</TableContainer>

						{footerHint && (
							<Typography sx={{ ...adminHintSx, mt: 1.5 }}>
								{footerHint}
							</Typography>
						)}
					</Paper>
				)}
			</Box>
		</>
	)

	if (embedded) return body
	return (
		<MbPage title={title} subtitle={subtitle}>
			{body}
		</MbPage>
	)
}
